import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import { useCallStore, selectIncomingCall } from '../store/callStore';
import { useVaultStore } from '../store/vaultStore';
import { callTabCoordination } from '../services/callTabCoordination';
import { isWebViewMode } from '../utils/webviewBridge';

export function IncomingCallPassiveBanner() {
  if (isWebViewMode) {
    return null;
  }

  return <IncomingCallPassiveBannerContent />;
}

function IncomingCallPassiveBannerContent() {
  const incomingCall = useCallStore(selectIncomingCall);
  const vaultData = useVaultStore((s) => s.vaultData);
  const { t } = useI18n();
  const [isLeader, setIsLeader] = useState(() => callTabCoordination.getSnapshot(incomingCall?.callSessionId).isLeader);

  useEffect(() => {
    setIsLeader(callTabCoordination.getSnapshot(incomingCall?.callSessionId).isLeader);

    return callTabCoordination.subscribe((event) => {
      if (!incomingCall?.callSessionId) {
        setIsLeader(false);
        return;
      }
      if (event.callSessionId !== incomingCall.callSessionId) return;
      setIsLeader(callTabCoordination.getSnapshot(incomingCall.callSessionId).isLeader);
    });
  }, [incomingCall?.callSessionId]);

  const displayName = useMemo(() => {
    if (!incomingCall) return '';
    if (!vaultData) return incomingCall.fromDisplayName || t('common.unknown');

    const fromId = (incomingCall.fromIdentityId || '').trim();
    const fromKey = (incomingCall.fromIdentityPublicKey?.value || '').trim();
    const contacts = Object.values(vaultData.contacts || {});

    for (const contact of contacts) {
      const has = contact.identities.some((identity) => {
        const identityId = (identity.identityId || '').trim();
        const identityKey = (identity.identityPublicKey.value || '').trim();
        if (fromKey && identityKey && identityKey === fromKey) return true;
        if (fromId && identityId && identityId === fromId) return true;
        return false;
      });
      if (has) return contact.displayName;
    }

    return incomingCall.fromDisplayName || t('common.unknown');
  }, [incomingCall, vaultData, t]);

  if (!incomingCall || isLeader) {
    return null;
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-amber-900">
            {t('incomingCall.otherTabTitle')}
          </div>
          <div className="text-sm text-amber-800 truncate">
            {t('incomingCall.otherTabBody', { name: displayName || t('common.unknown') })}
          </div>
        </div>
      </div>
    </div>
  );
}
